import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  debounce,
  IconButton,
  Typography,
} from "@mui/material";
import { palette } from "../../themes/theme";
import { forwardRef, useState } from "react";
import DarkPrimaryButton from "../PrimaryButton/DarkPrimaryButton";
import { CustomDraggable } from "../Draggable";
import PrimaryTextField from "../PrimaryTextField";
import { a11yProps, StyledTab, StyledTabs, TabPanel } from "../Tabs";
import DragAndDropFileUpload from "../DragAndDropFileUpload";
import mapboxgl from "mapbox-gl";
import { useDispatch, useSelector } from "react-redux";
import { CustomPolygonClass } from "./utilities/CustomPolygon";
import { CustomLogoClass } from "./utilities/CustomLogo";
import { fetchEmojiIcons, mapLogoUpload, postImageSearch } from "../../api";
import { showToaster } from "../../redux/slices/toastSlice";

const LogoTabsEnum = {
  LIBRARY: 0,
  UPLOAD: 1,
  EMOJI: 2,
};

const CustomListbox = forwardRef(function CustomListbox(props, ref) {
  return (
    <ul
      ref={ref}
      {...props}
      style={{
        ...props.style,
        maxHeight: 100, // Set your desired max height
        overflowY: "auto",
        color: palette.text.white,
      }}
    />
  );
});

export const LogoSetting = ({
  mapRef,
  handleClose,
  drawRef,
  imageCoordinates,
  lastClickButtonRef,
  setMapToolBarOptionFlags,
  setMarkers,
  createNewPolygon,
  resetAllOnceChanged,
  currentMode,
  disableUserActions,
  getCurrentPoiSetting,
  setSelectedLogo,
  defaultLogoValue,
  markerRef,
}) => {
  const logoStyleSettings = useSelector(
    (state) => state.mapSettings.logoSetting
  );
  const dispatch = useDispatch();

  const s3Path = process.env.REACT_APP_ASSETS_URL + "poi/svg/";

  // const [activeTab, setActiveTab] = useState(LogoTabsEnum.LIBRARY);
  const [activeEmoji, setActiveEmoji] = useState("");
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uFiles, setUFiles] = useState(null);
  const [logoOptions, setLogoOptions] = useState([]);
  const [iconNames, setIconNames] = useState([
    { key: "fa-solid fa-apartment" },
    { key: "fa-solid fa-house" },
    { key: "fa-solid fa-hotel" },
    { key: "fa-solid fa-hospital" },
    { key: "fa-solid fa-school" },
    { key: "fa-sharp fa-solid fa-database" },
    { key: "fa-solid fa-building" },
    { key: "fa-solid fa-utensils" },
    { key: "fa-sharp fa-solid fa-industry-windows" },
    { key: "fa-solid fa-gas-pump" },
    { key: "fa-solid fa-dumbbell" },
    { key: "fa-duotone fa-solid fa-traffic-light-stop" },
    { key: "fa-solid fa-tree" },
    { key: "fa-sharp fa-solid fa-triangle-person-digging" },
    { key: "fa-solid fa-truck" },
    { key: "fa-solid fa-cars" },
    { key: "fa-solid fa-bicycle" },
    { key: "fa-solid fa-tractor" },
    { key: "fa-solid fa-cart-shopping" },
    { key: "fa-solid fa-person" },
    { key: "fa-solid fa-campfire" },
    { key: "fa-solid fa-xmark" },
    { key: "fa-solid fa-check" },
    { key: "fa-solid fa-circle-parking" },
    { key: "fa-solid fa-landmark-dome" },
  ]);

  const searchEmoji = debounce(async (e) => {
    setLoading(true);
    const value = e.target.value.trim();
    if (!value) {
      setLoading(false);
      return;
    }
    const res = await fetchEmojiIcons(value);
    setLoading(false);
    if (!res?.data) return;
    setIconNames(
      res.data
        .flatMap((d) => d.styles.map((x) => `fa-${x} fa-${d.id}`))
        .splice(0, 50)
        .map((d) => ({ key: d }))
    );
  }, 1000);

  const searchLogo = debounce(async (e) => {
    setLoading(true);
    const value = e.target.value.trim();
    if (!value) {
      setLoading(false);
      return;
    }
    const res = await postImageSearch({
      body: { value },
    });
    setLoading(false);
    if (!res?.data) return;
    setLogoOptions(
      res.data.poiImages.map((d) => ({
        label: d.poiName,
        retailerId: d.retailerId,
      }))
    );
  }, 1000);

  const handleFileUpload = (files) => {
    setUFiles(files);
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setPreview(e.target.result);
      }
    };
    reader.readAsDataURL(files[0]);
  };

  const handleUpload = async () => {
    setUploadLoading(true);
    try {
      if (!uFiles || uFiles.length <= 0) {
        throw "no files";
      }
      const formData = new FormData();
      formData.append("file", uFiles[0]);
      await mapLogoUpload({
        body: formData,
        conifg: { headers: { "Content-Type": "multipart/form-data" } },
      });
    } catch (err) {
      console.log(err);
      dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: "Something went wrong please try again",
          showDialog: true,
          timer: "5000",
        })
      );
      setUploadLoading(false);
      return;
    }
    await CustomLogoClass.AddLogoToMap({
      mapboxgl,
      map: mapRef,
      mapDraw: drawRef,
      coordinates: imageCoordinates,
      logo: preview,
      lastClickButton: lastClickButtonRef,
      logoDefaultStyle: logoStyleSettings,
      defaultLogoValue,
      setSelectedLogo,
      setMapToolBarOptionFlags,
      setResetMap: () => {},
      createNewPolygon,
      updateAutoSave: () => {},
      resetAllOnceChanged,
      poiImageId: null,
      currentMode,
      polygonDefaultStyle: CustomPolygonClass.polygonDefaultStyle,
      getCurrentPoiSetting,
      setDrawnFeaturesSupport: () => {},
      setUndoHistory: () => {},
      setRedoHistory: () => {},
      disableUserActions,
      markerRef,
    });
    setUploadLoading(false);
    setMarkers(markerRef.current);
    handleClose();
  };

  return (
    <CustomDraggable
      width={400}
      height={340}
      headerTitle={"Upload Logo"}
      customFooter={
        <>
          <Button
            loading={uploadLoading}
            disabled={uploadLoading}
            loadingPosition="start"
            onClick={handleUpload}
            variant="outlined"
            sx={{ fontWeight: "bold" }}
          >
            Upload
          </Button>
          {preview && (
            <Button onClick={() => setPreview("")} variant="contained" sx={{ fontWeight: "bold", color: palette.text.white }}>
              Change logo?
            </Button>
          )}
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ fontWeight: "bold" }}
          >
            Close
          </Button>
        </>
      }
    >
      <Box
        sx={{
          height: "calc(100% - 36px)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Box
            sx={{
              mt: 1,
              height: 140,
              display: "flex",
              justifyContent: "center",
            }}
          >
            {preview ? (
              <>
                <Box sx={{ height: "100%" }}>
                  <img
                    src={preview}
                    style={{ height: "inherit" }}
                    alt={"REBLIE"}
                  />
                </Box>
              </>
            ) : (
              <DragAndDropFileUpload onFileUpload={handleFileUpload} />
            )}
          </Box>
        </Box>
      </Box>
    </CustomDraggable>
  );
};
